import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Briefcase, CheckCircle, Upload, Loader } from 'lucide-react';
import './JobDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobDetail = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    portfolio_url: '',
    cover_letter: ''
  });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    fetchJobDetail();
  }, [jobId]);

  const fetchJobDetail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers/${jobId}`);
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only PDF, DOC, DOCX files are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size should be less than 5MB');
        return;
      }
      setResume(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!resume) {
      setError('Please upload your resume');
      return;
    }
    
    setSubmitting(true);
    
    const submitData = new FormData();
    submitData.append('applicant_name', formData.applicant_name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('portfolio_url', formData.portfolio_url);
    submitData.append('cover_letter', formData.cover_letter);
    submitData.append('resume', resume);
    
    try {
      const response = await fetch(`${API_URL}/api/careers/${jobId}/apply`, {
        method: 'POST',
        body: submitData
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-page">
        <div className="container">
          <div className="not-found">
            <h2>Job Not Found</h2>
            <p>This position may have been filled or removed.</p>
            <Link to="/careers" className="btn btn-primary">View All Jobs</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail-page">
      {/* Hero */}
      <section className="job-hero">
        <div className="container">
          <Link to="/careers" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to All Jobs</span>
          </Link>
          <h1 className="job-hero-title">{job.job_title}</h1>
          <div className="job-hero-meta">
            <span><MapPin size={18} /> {job.location}</span>
            <span><Clock size={18} /> {job.job_type}</span>
            <span><Briefcase size={18} /> {job.experience}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="job-content section">
        <div className="container">
          <div className="job-layout">
            {/* Job Details */}
            <div className="job-details">
              <div className="job-section">
                <h2>Job Description</h2>
                <p>{job.description}</p>
              </div>
              
              {job.requirements && (
                <div className="job-section">
                  <h2>Requirements</h2>
                  <div className="requirements-list">
                    {job.requirements.split('\n').map((req, index) => (
                      req.trim() && (
                        <div key={index} className="requirement-item">
                          <CheckCircle size={18} />
                          <span>{req.trim()}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Apply Section */}
            <div className="apply-sidebar">
              {!showForm && !submitted && (
                <div className="apply-card">
                  <h3>Interested in this role?</h3>
                  <p>Submit your application and we'll get back to you soon.</p>
                  <button 
                    className="btn btn-primary btn-apply"
                    onClick={() => setShowForm(true)}
                  >
                    Apply Now
                  </button>
                </div>
              )}

              {showForm && !submitted && (
                <div className="apply-form-card">
                  <h3>Apply for {job.job_title}</h3>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        name="applicant_name"
                        value={formData.applicant_name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Portfolio URL (Optional)</label>
                      <input
                        type="url"
                        name="portfolio_url"
                        value={formData.portfolio_url}
                        onChange={handleInputChange}
                        placeholder="https://your-portfolio.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Resume *</label>
                      <div className="file-upload">
                        <input
                          type="file"
                          id="resume"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="resume" className="file-upload-label">
                          <Upload size={20} />
                          <span>{resume ? resume.name : 'Upload Resume (PDF, DOC, DOCX)'}</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Cover Letter (Optional)</label>
                      <textarea
                        name="cover_letter"
                        value={formData.cover_letter}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Tell us why you're the perfect fit..."
                      />
                    </div>
                    
                    {error && <div className="error-message">{error}</div>}
                    
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-submit"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader size={18} className="spinning" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        'Submit Application'
                      )}
                    </button>
                  </form>
                </div>
              )}

              {submitted && (
                <div className="success-card">
                  <CheckCircle size={48} />
                  <h3>Application Submitted!</h3>
                  <p>Thank you for applying. We'll review your application and get back to you soon.</p>
                  <Link to="/careers" className="btn btn-secondary">
                    View Other Jobs
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;
